import { AnimatePresence, motion } from 'framer-motion';
import { type ReactNode } from 'react';
import { type UseFormRegisterReturn } from 'react-hook-form';

/**
 * The parts every lead form on the site repeats: the spam honeypot, the
 * submission error banner, and the wrapping `<form>` itself.
 *
 * The forms differ genuinely in their fields — this is not the services pages,
 * where the whole file was duplicated — so only the scaffolding is shared.
 * Fields, consent copy and submit button stay with each form, since those are
 * what actually differ.
 *
 * Used by the contact, careers and services forms. The property enquiry card
 * deliberately does NOT use it: its error banner is a plain `<p>` with no
 * entry animation, and its honeypot is the `company` field rather than
 * `website`. Routing it through here would change what a user sees, which is
 * the one thing this refactor must not do.
 */
export function FormShell({
  onSubmit,
  ariaLabel,
  honeypot,
  error,
  reduce,
  className = 'flex flex-col gap-5',
  errorClassName = 'rounded border border-danger/30 bg-danger/10 px-3.5 py-2.5 text-sm text-danger',
  children,
  footer,
}: {
  onSubmit: (e: React.FormEvent) => void;
  ariaLabel?: string;
  /** `register('website')` — the off-screen field bots fill and humans never see. */
  honeypot: UseFormRegisterReturn;
  /** Submission failure message, or null when the last attempt was fine. */
  error: string | null;
  reduce: boolean;
  className?: string;
  errorClassName?: string;
  /** Fields — everything above the error banner. */
  children: ReactNode;
  /** Submit button — rendered below the error banner, as every form does. */
  footer: ReactNode;
}) {
  return (
    <form onSubmit={onSubmit} className={className} noValidate aria-label={ariaLabel}>
      {/* Honeypot — off-screen, hidden from users and assistive tech. */}
      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="absolute left-[-9999px] h-0 w-0 opacity-0"
        {...honeypot}
      />

      {children}

      <AnimatePresence>
        {error && (
          <motion.p
            initial={reduce ? false : { opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={errorClassName}
            role="alert"
          >
            {error}
          </motion.p>
        )}
      </AnimatePresence>

      {footer}
    </form>
  );
}
